import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Cookie, X, Settings } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { COOKIE_CONSENT_KEY, readCookiePreferences, saveCookiePreferences } from "@/lib/cookieConsent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
}


export function CookieConsent() {
  const { pathname } = useLocation();
  const isCampaignRoute = pathname.endsWith("/campagne");
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const saved = readCookiePreferences();
    if (saved) setPreferences(saved);

    // Show banner if no choice was made yet
    let consent: string | null = null;
    try { consent = localStorage.getItem(COOKIE_CONSENT_KEY); } catch { /* blocked */ }
    if (!consent) {
      // Delay showing banner by 1 second for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const saveConsent = (prefs: CookiePreferences) => {
    // Shared writer: same keys the pixel loader and all tracking checks read
    setPreferences(saveCookiePreferences(prefs));
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      functional: true,
      analytics: true,
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      functional: false,
      analytics: false,
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) {
    return null;
  }

  // Advertentie-landingspagina's (/vacatures/:slug/campagne): alleen een smalle balk,
  // zodat de vacature en de "Reageer direct"-knop zichtbaar blijven. Op mobiel staat hij
  // bovenaan over de kopbalk (logo/menu), zodat hij nooit over "In het kort" of de
  // sollicitatiebalk valt; zolang het sollicitatievenster open is, wijkt hij (dat venster
  // staat dan bovenaan). Weigeren blijft net zo makkelijk als accepteren.
  if (isCampaignRoute) {
    return (
      <div
        role="region"
        aria-label="Cookiemelding"
        className="fixed inset-x-3 z-[60] [body[data-scroll-locked]_&]:hidden top-[calc(0.5rem+env(safe-area-inset-top))] sm:top-auto sm:bottom-4 sm:left-auto sm:right-4 sm:w-[26rem] animate-in fade-in slide-in-from-top-2 sm:slide-in-from-bottom-2 duration-300"
      >
        <div className="flex items-center gap-2 rounded-2xl border border-border bg-background/95 backdrop-blur px-3 py-2">
          <p className="flex-1 min-w-0 text-xs leading-snug text-muted-foreground">
            🍪 We gebruiken cookies.{" "}
            <Link to="/privacy-policy" className="font-medium text-foreground underline underline-offset-2">
              Privacy
            </Link>
          </p>
          <Button onClick={acceptNecessary} variant="ghost" size="sm" className="h-8 px-2.5 text-xs shrink-0">
            Alleen nodig
          </Button>
          <Button onClick={acceptAll} size="sm" className="h-8 px-3 text-xs shrink-0">
            Oké
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 max-md:p-2 max-md:pb-[calc(0.5rem+env(safe-area-inset-bottom))] animate-in slide-in-from-bottom-5 duration-500">
      <Card className="max-w-4xl mx-auto p-6 max-md:p-3 shadow-2xl border-2">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-primary/10 flex-shrink-0 max-md:hidden">
            <Cookie className="w-6 h-6 text-primary" />
          </div>

          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-4 max-md:mb-2 max-md:gap-2">
              <div>
                <h3 className="text-xl font-heading font-bold mb-2 max-md:hidden">
                  🍪 We gebruiken cookies
                </h3>
                <p className="text-sm text-foreground/80 leading-relaxed">
                  <span className="max-md:hidden">We gebruiken cookies om je ervaring te verbeteren en onze diensten te optimaliseren. 
                  Noodzakelijke cookies zijn altijd actief om het platform te laten werken.{" "}</span>
                  <span className="md:hidden">🍪 We gebruiken cookies om ChillWerk te verbeteren.{" "}</span>
                  <Link to="/privacy-policy" className="text-primary hover:underline font-medium">
                    Lees ons privacybeleid
                  </Link>
                  .
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowBanner(false)}
                className="flex-shrink-0 max-md:h-11 max-md:w-11 max-md:-mt-2 max-md:-mr-2"
                aria-label="Sluit cookie melding"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-3 max-md:grid max-md:grid-cols-2 max-md:gap-2">
              <Button onClick={acceptAll} size="sm" className="flex-1 sm:flex-initial">
                Accepteer Alles
              </Button>
              
              <Button 
                onClick={acceptNecessary} 
                variant="outline" 
                size="sm"
                className="flex-1 sm:flex-initial"
              >
                <span className="max-md:hidden">Alleen Noodzakelijke</span>
                <span className="md:hidden">Alleen nodig</span>
              </Button>

              <Dialog open={showSettings} onOpenChange={setShowSettings}>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex-1 sm:flex-initial max-md:col-span-2 max-md:h-9">
                    <Settings className="w-4 h-4 mr-2" />
                    Voorkeuren
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Cookie Voorkeuren</DialogTitle>
                    <DialogDescription>
                      Pas je cookie voorkeuren aan. Noodzakelijke cookies kunnen niet worden uitgeschakeld.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-6 py-4">
                    {/* Necessary */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Label className="font-bold">Noodzakelijke Cookies</Label>
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                            Verplicht
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Essentieel voor de werking van het platform, zoals login en voorkeuren.
                        </p>
                      </div>
                      <Switch checked={true} disabled />
                    </div>

                    {/* Functional */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <Label className="font-bold">Functionele Cookies</Label>
                        <p className="text-sm text-muted-foreground">
                          Voor het onthouden van je instellingen en voorkeuren.
                        </p>
                      </div>
                      <Switch
                        checked={preferences.functional}
                        onCheckedChange={(checked) =>
                          setPreferences({ ...preferences, functional: checked })
                        }
                      />
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <Label className="font-bold">Analytische Cookies</Label>
                        <p className="text-sm text-muted-foreground">
                          Helpen ons het platform te verbeteren door anoniem gebruiksgedrag te analyseren.
                        </p>
                      </div>
                      <Switch
                        checked={preferences.analytics}
                        onCheckedChange={(checked) =>
                          setPreferences({ ...preferences, analytics: checked })
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={savePreferences} className="flex-1">
                      Voorkeuren Opslaan
                    </Button>
                    <Button
                      onClick={() => setShowSettings(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Annuleren
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
